import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { 
  DollarSign, TrendingUp, AlertTriangle, FileText, 
  Plus, Edit2, Trash2, Send, Eye, Download,
  Building2, FolderKanban, CreditCard, Settings,
  ArrowUpRight, ArrowDownRight, RefreshCw, Ban, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { InvoiceViewDialog } from '../components/billing/InvoiceViewDialog';
import { RecordPaymentDialog } from '../components/billing/RecordPaymentDialog';
import { InvoiceCreateDialog } from '../components/billing/InvoiceCreateDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Add billing API methods
const billingApi = {
  getTiers: () => axios.get(`${BACKEND_URL}/api/billing/tiers`),
  createTier: (data) => axios.post(`${BACKEND_URL}/api/billing/tiers`, data),
  updateTier: (id, data) => axios.put(`${BACKEND_URL}/api/billing/tiers/${id}`, data),
  deleteTier: (id) => axios.delete(`${BACKEND_URL}/api/billing/tiers/${id}`),
  
  getDashboard: () => axios.get(`${BACKEND_URL}/api/billing/dashboard`),
  getForecast: (months = 6) => axios.get(`${BACKEND_URL}/api/billing/forecast?months=${months}`),
  
  getInvoices: (filters = {}) => {
    const params = new URLSearchParams(filters).toString();
    return axios.get(`${BACKEND_URL}/api/billing/invoices${params ? '?' + params : ''}`);
  },
  createInvoice: (data) => axios.post(`${BACKEND_URL}/api/billing/invoices`, data),
  updateInvoice: (id, data) => axios.put(`${BACKEND_URL}/api/billing/invoices/${id}`, data),
  sendInvoice: (id) => axios.post(`${BACKEND_URL}/api/billing/invoices/${id}/send`),
  resendInvoice: (id) => axios.post(`${BACKEND_URL}/api/billing/invoices/${id}/resend`),
  voidInvoice: (id) => axios.post(`${BACKEND_URL}/api/billing/invoices/${id}/void`),
  getCreditNotes: (clientId) => axios.get(`${BACKEND_URL}/api/billing/credit-notes${clientId ? `?client_id=${clientId}` : ''}`),
  createCreditNote: (data) => axios.post(`${BACKEND_URL}/api/billing/credit-notes`, data),
  getStatement: (clientId, fromDate) => axios.get(`${BACKEND_URL}/api/billing/statements/${clientId}${fromDate ? `?from_date=${fromDate}` : ''}`),
  sendStatement: (clientId) => axios.post(`${BACKEND_URL}/api/billing/statements/${clientId}/send`),
  
  getSubscriptions: () => axios.get(`${BACKEND_URL}/api/billing/subscriptions`),
  createSubscription: (data) => axios.post(`${BACKEND_URL}/api/billing/subscriptions`, data),
  updateSubscription: (id, data) => axios.put(`${BACKEND_URL}/api/billing/subscriptions/${id}`, data),
  
  recordPayment: (data) => axios.post(`${BACKEND_URL}/api/billing/payments`, data),
  
  getSettings: () => axios.get(`${BACKEND_URL}/api/billing/settings`),
  updateSettings: (data) => axios.put(`${BACKEND_URL}/api/billing/settings`, data),
  
  getStatement: (clientId) => axios.get(`${BACKEND_URL}/api/billing/statements/${clientId}`),
  
  generateMonthlyInvoices: () => axios.post(`${BACKEND_URL}/api/billing/generate-monthly-invoices`),
};

function formatCurrency(amount, symbol = 'R') {
  return `${symbol}${amount?.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
}

export default function BillingDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [dashboard, setDashboard] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [forecast, setForecast] = useState([]);
  const [settings, setSettings] = useState(null);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [creditNotes, setCreditNotes] = useState([]);
  const [selectedStatementClient, setSelectedStatementClient] = useState('');
  const [statementData, setStatementData] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  
  // Dialog states
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState({
    name: '',
    min_value: '',
    max_value: '',
    monthly_price: '',
    annual_discount_percentage: 0,
    description: ''
  });
  
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [viewInvoiceOpen, setViewInvoiceOpen] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editInvoiceOpen, setEditInvoiceOpen] = useState(false);
  
  // Subscription form
  const [subscriptionForm, setSubscriptionForm] = useState({
    project_id: '',
    client_id: '',
    billing_tier_id: '',
    billing_start_date: new Date().toISOString().split('T')[0],
    billing_cycle: 'monthly',
    discount_percentage: 0,
    billing_override_amount: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, tiersRes, invoicesRes, subsRes, forecastRes, settingsRes, clientsRes, projectsRes, cnRes] = await Promise.all([
        billingApi.getDashboard(),
        billingApi.getTiers(),
        billingApi.getInvoices(),
        billingApi.getSubscriptions(),
        billingApi.getForecast(),
        billingApi.getSettings(),
        api.clients.getAll(),
        api.projects.getAll(),
        billingApi.getCreditNotes()
      ]);
      
      setDashboard(dashRes.data);
      setTiers(tiersRes.data);
      setInvoices(invoicesRes.data);
      setSubscriptions(subsRes.data);
      setForecast(forecastRes.data);
      setSettings(settingsRes.data);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
      setCreditNotes(cnRes.data || []);
    } catch (error) {
      console.error('Failed to load billing data:', error);
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  // Tier handlers
  const handleSaveTier = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...tierForm,
        min_value: parseFloat(tierForm.min_value),
        max_value: tierForm.max_value ? parseFloat(tierForm.max_value) : null,
        monthly_price: parseFloat(tierForm.monthly_price),
        annual_discount_percentage: parseFloat(tierForm.annual_discount_percentage) || 0
      };
      
      if (editingTier) {
        await billingApi.updateTier(editingTier.id, data);
        toast.success('Tier updated');
      } else {
        await billingApi.createTier(data);
        toast.success('Tier created');
      }
      
      setTierDialogOpen(false);
      setEditingTier(null);
      setTierForm({ name: '', min_value: '', max_value: '', monthly_price: '', annual_discount_percentage: 0, description: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to save tier');
    }
  };

  const handleEditTier = (tier) => {
    setEditingTier(tier);
    setTierForm({
      name: tier.name,
      min_value: tier.min_value.toString(),
      max_value: tier.max_value?.toString() || '',
      monthly_price: tier.monthly_price.toString(),
      annual_discount_percentage: tier.annual_discount_percentage || 0,
      description: tier.description || ''
    });
    setTierDialogOpen(true);
  };

  const handleDeleteTier = async (tier) => {
    if (!window.confirm(`Delete tier "${tier.name}"?`)) return;
    try {
      await billingApi.deleteTier(tier.id);
      toast.success('Tier deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete tier');
    }
  };

  // Subscription handlers
  const handleCreateSubscription = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...subscriptionForm,
        discount_percentage: parseFloat(subscriptionForm.discount_percentage) || 0,
        billing_override_amount: subscriptionForm.billing_override_amount 
          ? parseFloat(subscriptionForm.billing_override_amount) 
          : null
      };
      
      await billingApi.createSubscription(data);
      toast.success('Subscription created');
      setSubscriptionDialogOpen(false);
      setSubscriptionForm({
        project_id: '',
        client_id: '',
        billing_tier_id: '',
        billing_start_date: new Date().toISOString().split('T')[0],
        billing_cycle: 'monthly',
        discount_percentage: 0,
        billing_override_amount: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
    }
  };

  const handleProjectSelect = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      // Auto-select client
      setSubscriptionForm(prev => ({
        ...prev,
        project_id: projectId,
        client_id: project.client_id || ''
      }));
      
      // Auto-select tier based on contract value
      if (project.contract_value) {
        const matchingTier = tiers.find(t => {
          const minOk = project.contract_value >= t.min_value;
          const maxOk = t.max_value === null || project.contract_value <= t.max_value;
          return minOk && maxOk && t.is_active;
        });
        if (matchingTier) {
          setSubscriptionForm(prev => ({
            ...prev,
            billing_tier_id: matchingTier.id
          }));
        }
      }
    }
  };

  const getProjectsWithoutSubscription = () => {
    const subscribedProjectIds = subscriptions.map(s => s.project_id);
    return projects.filter(p => !subscribedProjectIds.includes(p.id));
  };

  // Generate invoices
  const handleGenerateInvoices = async () => {
    if (!window.confirm('Generate monthly invoices for all active subscriptions?')) return;
    try {
      const response = await billingApi.generateMonthlyInvoices();
      toast.success(`Generated ${response.data.count} invoices`);
      loadData();
    } catch (error) {
      toast.error('Failed to generate invoices');
    }
  };

  // Invoice actions
  const handleCreateInvoice = async (payload) => {
    try {
      await billingApi.createInvoice(payload);
      toast.success('Invoice created');
      setInvoiceDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleSendInvoice = async (invoice) => {
    if (!window.confirm(`Send invoice ${invoice.invoice_number} to ${invoice.client_email || 'client'}?`)) return;
    try {
      await billingApi.sendInvoice(invoice.id);
      toast.success('Invoice sent successfully');
      loadData();
      setViewInvoiceOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
    }
  };

  const handleResendInvoice = async (invoice) => {
    if (!window.confirm(`Resend invoice ${invoice.invoice_number}?`)) return;
    try {
      await billingApi.resendInvoice(invoice.id);
      toast.success('Invoice resent successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invoice');
    }
  };

  const handleVoidInvoice = async (invoice) => {
    if (!window.confirm(`Void invoice ${invoice.invoice_number}? This cannot be undone.`)) return;
    try {
      await billingApi.voidInvoice(invoice.id);
      toast.success('Invoice voided');
      loadData();
      setViewInvoiceOpen(false);
    } catch (error) {
      toast.error('Failed to void invoice');
    }
  };

  const handleRecordPayment = async (paymentData) => {
    try {
      await billingApi.recordPayment(paymentData);
      toast.success('Payment recorded successfully');
      setPaymentDialogOpen(false);
      setPaymentInvoice(null);
      setViewInvoiceOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openViewInvoice = (invoice) => {
    setViewInvoice(invoice);
    setViewInvoiceOpen(true);
  };

  const openPaymentDialog = (invoice) => {
    setPaymentInvoice(invoice);
    setPaymentDialogOpen(true);
  };

  const openEditInvoice = (invoice) => {
    setEditInvoice(invoice);
    setEditInvoiceOpen(true);
  };

  const handleUpdateInvoice = async (invoiceId, updateData) => {
    try {
      await billingApi.updateInvoice(invoiceId, updateData);
      toast.success('Invoice updated');
      setEditInvoiceOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  // Statement handlers
  const loadStatement = async (clientId) => {
    if (!clientId) return;
    setStatementLoading(true);
    try {
      const res = await billingApi.getStatement(clientId);
      setStatementData(res.data);
    } catch (error) {
      toast.error('Failed to load statement');
    } finally {
      setStatementLoading(false);
    }
  };

  const handleSendStatement = async (clientId) => {
    if (!window.confirm('Send statement email to client?')) return;
    try {
      await billingApi.sendStatement(clientId);
      toast.success('Statement sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send statement');
    }
  };

  // Credit Note handlers
  const [cnDialogOpen, setCnDialogOpen] = useState(false);
  const [cnForm, setCnForm] = useState({ client_id: '', invoice_id: '', amount: '', reason: '' });

  const handleCreateCreditNote = async (e) => {
    e.preventDefault();
    try {
      await billingApi.createCreditNote({
        client_id: cnForm.client_id,
        invoice_id: cnForm.invoice_id || undefined,
        amount: parseFloat(cnForm.amount),
        reason: cnForm.reason,
      });
      toast.success('Credit note created');
      setCnDialogOpen(false);
      setCnForm({ client_id: '', invoice_id: '', amount: '', reason: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create credit note');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-700';
      case 'sent': return 'bg-blue-100 text-blue-700';
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'overdue': return 'bg-red-100 text-red-700';
      case 'partially_paid': return 'bg-yellow-100 text-yellow-700';
      case 'void': return 'bg-slate-100 text-slate-500';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-500">Loading billing data...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-barlow font-bold uppercase tracking-tight">Billing & Accounting</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Manage subscriptions, invoices, and revenue</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setSettingsDialogOpen(true)}>
              <Settings className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Settings</span>
            </Button>
            <Button size="sm" onClick={handleGenerateInvoices} className="bg-slate-900 text-white">
              <FileText className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Generate Invoices</span>
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Monthly Recurring Revenue</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(dashboard?.mrr)}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-3 flex items-center text-sm">
              {dashboard?.mrr > dashboard?.revenue_last_month ? (
                <>
                  <ArrowUpRight className="h-4 w-4 text-green-500 mr-1" />
                  <span className="text-green-600">Growth</span>
                </>
              ) : (
                <>
                  <ArrowDownRight className="h-4 w-4 text-red-500 mr-1" />
                  <span className="text-red-600">Decline</span>
                </>
              )}
              <span className="text-slate-500 ml-2">vs last month</span>
            </div>
          </div>

          <div className="bg-white border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Active Subscriptions</p>
                <p className="text-3xl font-bold mt-1">{dashboard?.active_subscriptions || 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FolderKanban className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              {dashboard?.active_projects || 0} active projects
            </p>
          </div>

          <div className="bg-white border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Outstanding</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(dashboard?.total_outstanding)}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              {invoices.filter(i => i.status === 'sent' || i.status === 'overdue').length} unpaid invoices
            </p>
          </div>

          <div className="bg-white border rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Overdue</p>
                <p className="text-3xl font-bold mt-1 text-red-600">{formatCurrency(dashboard?.overdue_amount)}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-500">
              {dashboard?.overdue_invoices || 0} overdue invoices
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="statements">Statements</TabsTrigger>
            <TabsTrigger value="credit-notes">Credit Notes</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="tiers">Billing Tiers</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue by Tier */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="font-semibold mb-4">Revenue by Tier</h3>
                {Object.keys(dashboard?.revenue_by_tier || {}).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(dashboard.revenue_by_tier).map(([tier, revenue]) => (
                      <div key={tier} className="flex items-center justify-between">
                        <span className="text-sm font-medium">{tier}</span>
                        <span className="font-semibold">{formatCurrency(revenue)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No revenue data yet</p>
                )}
              </div>

              {/* Revenue Forecast */}
              <div className="bg-white border rounded-lg p-6">
                <h3 className="font-semibold mb-4">6-Month Forecast</h3>
                {forecast.length > 0 ? (
                  <div className="space-y-3">
                    {forecast.map((f) => (
                      <div key={f.month} className="flex items-center justify-between">
                        <span className="text-sm font-medium">{f.month}</span>
                        <div className="text-right">
                          <span className="font-semibold">{formatCurrency(f.projected_revenue)}</span>
                          <span className="text-xs text-slate-500 ml-2">({f.active_subscriptions} subs)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No forecast data</p>
                )}
              </div>
            </div>

            {/* Recent Invoices */}
            <div className="bg-white border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Recent Invoices</h3>
                <Button variant="link" onClick={() => setActiveTab('invoices')}>View All</Button>
              </div>
              {invoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Invoice</th>
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Client</th>
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Date</th>
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.slice(0, 5).map((invoice) => (
                        <tr key={invoice.id} className="border-b last:border-0">
                          <td className="py-3 font-mono text-sm">{invoice.invoice_number}</td>
                          <td className="py-3 text-sm">{invoice.client_name}</td>
                          <td className="py-3 text-sm text-slate-500">{invoice.invoice_date}</td>
                          <td className="py-3 font-semibold">{formatCurrency(invoice.total)}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${getStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-8">No invoices yet</p>
              )}
            </div>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="mt-6">
            <div className="bg-white border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">All Invoices</h3>
                <Button onClick={() => setInvoiceDialogOpen(true)} data-testid="new-invoice-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Invoice
                </Button>
              </div>
              {invoices.length > 0 ? (
                <InvoicesTableInline
                  invoices={invoices}
                  onViewInvoice={openViewInvoice}
                  onSendInvoice={handleSendInvoice}
                  onResendInvoice={handleResendInvoice}
                  onEditInvoice={openEditInvoice}
                  onRecordPayment={openPaymentDialog}
                  onVoidInvoice={handleVoidInvoice}
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No invoices yet</p>
                  <p className="text-sm text-slate-400 mt-1">Create subscriptions and generate invoices</p>
                </div>
              )}
            </div>
          </TabsContent>


          {/* Statements Tab */}
          <TabsContent value="statements" className="mt-6">
            <div className="bg-white border rounded-lg p-6">
              <h3 className="font-semibold mb-4">Client Statement of Account</h3>
              <div className="flex items-end gap-4 mb-6">
                <div className="flex-1">
                  <Label className="text-xs font-bold uppercase tracking-wider">Select Client</Label>
                  <Select value={selectedStatementClient} onValueChange={v => { setSelectedStatementClient(v); loadStatement(v); }}>
                    <SelectTrigger data-testid="statement-client-select"><SelectValue placeholder="Choose a client" /></SelectTrigger>
                    <SelectContent>
                      {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {selectedStatementClient && statementData && (
                  <>
                    <Button variant="outline" onClick={() => {
                      const link = document.createElement('a');
                      link.href = `${BACKEND_URL}/api/billing/statements/${selectedStatementClient}/pdf`;
                      link.target = '_blank';
                      link.click();
                    }} data-testid="download-statement-pdf-btn">
                      <Download className="h-4 w-4 mr-2" />Download PDF
                    </Button>
                    <Button variant="outline" onClick={() => handleSendStatement(selectedStatementClient)} data-testid="send-statement-btn">
                      <Send className="h-4 w-4 mr-2" />Send to Client
                    </Button>
                  </>
                )}
              </div>
              {statementLoading && <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
              {statementData && !statementLoading && (
                <div className="space-y-4" data-testid="statement-view">
                  {/* Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-slate-50 p-3 rounded-lg border"><p className="text-[10px] font-bold uppercase text-slate-400">Opening Balance</p><p className="text-lg font-bold">R{(statementData.opening_balance || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</p></div>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200"><p className="text-[10px] font-bold uppercase text-blue-500">Total Invoiced</p><p className="text-lg font-bold text-blue-700">R{(statementData.total_invoiced || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</p></div>
                    <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200"><p className="text-[10px] font-bold uppercase text-emerald-500">Total Payments</p><p className="text-lg font-bold text-emerald-700">R{(statementData.total_payments || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</p></div>
                    <div className="bg-red-50 p-3 rounded-lg border border-red-200"><p className="text-[10px] font-bold uppercase text-red-500">Closing Balance</p><p className="text-lg font-bold text-red-700">R{(statementData.closing_balance || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</p></div>
                  </div>
                  {/* Aging */}
                  <div className="bg-slate-50 p-3 rounded-lg border">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2">Aging Analysis</p>
                    <div className="grid grid-cols-4 gap-3 text-sm">
                      <div><span className="text-slate-500">Current:</span> <span className="font-semibold">R{(statementData.aging?.current || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</span></div>
                      <div><span className="text-slate-500">30 Days:</span> <span className="font-semibold">R{(statementData.aging?.['30_days'] || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</span></div>
                      <div><span className="text-slate-500">60 Days:</span> <span className="font-semibold">R{(statementData.aging?.['60_days'] || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</span></div>
                      <div><span className="text-red-600">90+ Days:</span> <span className="font-bold text-red-600">R{(statementData.aging?.['90_plus'] || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</span></div>
                    </div>
                  </div>
                  {/* Transactions */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-900 text-white">
                          <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-wider">Date</th>
                          <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-wider">Reference</th>
                          <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-wider">Description</th>
                          <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider">Debit</th>
                          <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider">Credit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(statementData.transactions || []).map((t, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50/50">
                            <td className="py-2.5 px-3 text-sm">{t.date}</td>
                            <td className="py-2.5 px-3 text-sm font-mono">{t.reference}</td>
                            <td className="py-2.5 px-3 text-sm">{t.description}</td>
                            <td className="py-2.5 px-3 text-sm text-right">{t.debit > 0 ? `R${t.debit.toLocaleString('en-ZA', {minimumFractionDigits:2})}` : '-'}</td>
                            <td className="py-2.5 px-3 text-sm text-right text-emerald-600">{t.credit > 0 ? `R${t.credit.toLocaleString('en-ZA', {minimumFractionDigits:2})}` : '-'}</td>
                          </tr>
                        ))}
                        {(statementData.transactions || []).length === 0 && (
                          <tr><td colSpan="5" className="py-8 text-center text-sm text-slate-400">No transactions found for this client</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!selectedStatementClient && !statementLoading && (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">Select a client to view their statement</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Credit Notes Tab */}
          <TabsContent value="credit-notes" className="mt-6">
            <div className="bg-white border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Credit Notes</h3>
                <Button onClick={() => setCnDialogOpen(true)} data-testid="new-credit-note-btn">
                  <Plus className="h-4 w-4 mr-2" />New Credit Note
                </Button>
              </div>
              {creditNotes.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Credit Note</th>
                        <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Client</th>
                        <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Invoice</th>
                        <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Date</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Amount</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">VAT</th>
                        <th className="text-right py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Total</th>
                        <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditNotes.map((cn) => {
                        const clientName = clients.find(c => c.id === cn.client_id)?.name || cn.client_id;
                        return (
                          <tr key={cn.id} className="border-b last:border-0 hover:bg-slate-50/50">
                            <td className="py-3 px-3 font-mono text-sm font-medium">{cn.credit_note_number}</td>
                            <td className="py-3 px-3 text-sm">{clientName}</td>
                            <td className="py-3 px-3 text-sm text-slate-500 font-mono">{cn.invoice_id ? invoices.find(i => i.id === cn.invoice_id)?.invoice_number || '-' : '-'}</td>
                            <td className="py-3 px-3 text-sm text-slate-500">{cn.issue_date}</td>
                            <td className="py-3 px-3 text-sm text-right">R{(cn.amount || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</td>
                            <td className="py-3 px-3 text-sm text-right text-slate-500">R{(cn.tax_amount || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</td>
                            <td className="py-3 px-3 text-sm text-right font-semibold text-red-600">R{(cn.total || 0).toLocaleString('en-ZA', {minimumFractionDigits:2})}</td>
                            <td className="py-3 px-3 text-sm text-slate-600">{cn.reason}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No credit notes issued yet</p>
                </div>
              )}
            </div>
            {/* Create Credit Note Dialog */}
            <Dialog open={cnDialogOpen} onOpenChange={setCnDialogOpen}>
              <DialogContent className="max-w-md" data-testid="create-credit-note-dialog">
                <DialogHeader>
                  <DialogTitle className="text-sm font-bold uppercase tracking-wider">Create Credit Note</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateCreditNote} className="space-y-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Client <span className="text-red-500">*</span></Label>
                    <Select value={cnForm.client_id} onValueChange={v => setCnForm(f => ({ ...f, client_id: v }))}>
                      <SelectTrigger data-testid="cn-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                      <SelectContent>
                        {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Against Invoice (optional)</Label>
                    <Select value={cnForm.invoice_id} onValueChange={v => setCnForm(f => ({ ...f, invoice_id: v }))}>
                      <SelectTrigger data-testid="cn-invoice-select"><SelectValue placeholder="Optional: link to invoice" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value=" ">No linked invoice</SelectItem>
                        {invoices.filter(i => i.client_id === cnForm.client_id).map(i => (
                          <SelectItem key={i.id} value={i.id}>{i.invoice_number} — R{(i.total || 0).toFixed(2)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Amount (excl. VAT) <span className="text-red-500">*</span></Label>
                    <Input type="number" step="0.01" value={cnForm.amount} onChange={e => setCnForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" data-testid="cn-amount" />
                    {cnForm.amount && <p className="text-xs text-slate-500 mt-1">VAT ({settings?.vat_rate || 15}%): R{((parseFloat(cnForm.amount) || 0) * ((settings?.vat_rate || 15) / 100)).toFixed(2)} | Total: R{((parseFloat(cnForm.amount) || 0) * (1 + (settings?.vat_rate || 15) / 100)).toFixed(2)}</p>}
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Reason <span className="text-red-500">*</span></Label>
                    <Textarea value={cnForm.reason} onChange={e => setCnForm(f => ({ ...f, reason: e.target.value }))} rows={2} placeholder="Reason for credit note" data-testid="cn-reason" />
                  </div>
                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={!cnForm.client_id || !cnForm.amount || !cnForm.reason} data-testid="create-cn-btn">
                    Create Credit Note
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="mt-6">
            <div className="bg-white border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">Project Subscriptions</h3>
                <Dialog open={subscriptionDialogOpen} onOpenChange={(open) => {
                  setSubscriptionDialogOpen(open);
                  if (!open) {
                    setSubscriptionForm({
                      project_id: '',
                      client_id: '',
                      billing_tier_id: '',
                      billing_start_date: new Date().toISOString().split('T')[0],
                      billing_cycle: 'monthly',
                      discount_percentage: 0,
                      billing_override_amount: '',
                      notes: ''
                    });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Subscription
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Add Project Subscription</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateSubscription} className="space-y-4">
                      <div>
                        <Label>Project <span className="text-red-500">*</span></Label>
                        <Select
                          value={subscriptionForm.project_id}
                          onValueChange={handleProjectSelect}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a project" />
                          </SelectTrigger>
                          <SelectContent>
                            {getProjectsWithoutSubscription().length > 0 ? (
                              getProjectsWithoutSubscription().map(project => (
                                <SelectItem key={project.id} value={project.id}>
                                  {project.name} {project.contract_value ? `(${formatCurrency(project.contract_value)})` : ''}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="none" disabled>All projects have subscriptions</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Client</Label>
                        <Select
                          value={subscriptionForm.client_id}
                          onValueChange={(value) => setSubscriptionForm(prev => ({ ...prev, client_id: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select client" />
                          </SelectTrigger>
                          <SelectContent>
                            {clients.map(client => (
                              <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label>Billing Tier <span className="text-red-500">*</span></Label>
                        <Select
                          value={subscriptionForm.billing_tier_id}
                          onValueChange={(value) => setSubscriptionForm(prev => ({ ...prev, billing_tier_id: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select billing tier" />
                          </SelectTrigger>
                          <SelectContent>
                            {tiers.filter(t => t.is_active).map(tier => (
                              <SelectItem key={tier.id} value={tier.id}>
                                {tier.name} - {formatCurrency(tier.monthly_price)}/mo
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Billing Start Date</Label>
                          <Input
                            type="date"
                            value={subscriptionForm.billing_start_date}
                            onChange={(e) => setSubscriptionForm(prev => ({ ...prev, billing_start_date: e.target.value }))}
                          />
                        </div>
                        <div>
                          <Label>Billing Cycle</Label>
                          <Select
                            value={subscriptionForm.billing_cycle}
                            onValueChange={(value) => setSubscriptionForm(prev => ({ ...prev, billing_cycle: value }))}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="monthly">Monthly</SelectItem>
                              <SelectItem value="annual">Annual</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Discount (%)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={subscriptionForm.discount_percentage}
                            onChange={(e) => setSubscriptionForm(prev => ({ ...prev, discount_percentage: e.target.value }))}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <Label>Override Amount (R)</Label>
                          <Input
                            type="number"
                            value={subscriptionForm.billing_override_amount}
                            onChange={(e) => setSubscriptionForm(prev => ({ ...prev, billing_override_amount: e.target.value }))}
                            placeholder="Leave empty to use tier price"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Notes</Label>
                        <Input
                          value={subscriptionForm.notes}
                          onChange={(e) => setSubscriptionForm(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="Optional notes..."
                        />
                      </div>
                      
                      <Button type="submit" className="w-full" disabled={!subscriptionForm.project_id || !subscriptionForm.billing_tier_id}>
                        Create Subscription
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              {subscriptions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Project</th>
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Client</th>
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Tier</th>
                        <th className="text-right py-2 text-xs font-semibold text-slate-500 uppercase">Monthly Fee</th>
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Next Invoice</th>
                        <th className="text-left py-2 text-xs font-semibold text-slate-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => {
                        const project = projects.find(p => p.id === sub.project_id);
                        const client = clients.find(c => c.id === sub.client_id);
                        const tier = tiers.find(t => t.id === sub.billing_tier_id);
                        return (
                          <tr key={sub.id} className="border-b last:border-0 hover:bg-slate-50">
                            <td className="py-3 text-sm font-medium">{project?.name || 'Unknown'}</td>
                            <td className="py-3 text-sm">{client?.name || 'Unknown'}</td>
                            <td className="py-3 text-sm">{tier?.name || 'Custom'}</td>
                            <td className="py-3 font-semibold text-right">
                              {formatCurrency(sub.billing_override_amount || sub.monthly_fee)}
                            </td>
                            <td className="py-3 text-sm text-slate-500">{sub.next_invoice_date}</td>
                            <td className="py-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${
                                sub.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {sub.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <CreditCard className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No subscriptions yet</p>
                  <p className="text-sm text-slate-400 mt-1">Add subscriptions to start billing projects</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Billing Tiers Tab */}
          <TabsContent value="tiers" className="mt-6">
            <div className="bg-white border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-semibold">Billing Tiers</h3>
                  <p className="text-sm text-slate-500">Configure pricing based on project contract value</p>
                </div>
                <Dialog open={tierDialogOpen} onOpenChange={(open) => {
                  setTierDialogOpen(open);
                  if (!open) {
                    setEditingTier(null);
                    setTierForm({ name: '', min_value: '', max_value: '', monthly_price: '', annual_discount_percentage: 0, description: '' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Tier
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingTier ? 'Edit Tier' : 'Add Billing Tier'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSaveTier} className="space-y-4">
                      <div>
                        <Label>Tier Name</Label>
                        <Input
                          value={tierForm.name}
                          onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
                          placeholder="e.g., Tier 1"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Min Contract Value (R)</Label>
                          <Input
                            type="number"
                            value={tierForm.min_value}
                            onChange={(e) => setTierForm({ ...tierForm, min_value: e.target.value })}
                            placeholder="5000000"
                            required
                          />
                        </div>
                        <div>
                          <Label>Max Contract Value (R)</Label>
                          <Input
                            type="number"
                            value={tierForm.max_value}
                            onChange={(e) => setTierForm({ ...tierForm, max_value: e.target.value })}
                            placeholder="Leave empty for unlimited"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Monthly Price (R)</Label>
                          <Input
                            type="number"
                            value={tierForm.monthly_price}
                            onChange={(e) => setTierForm({ ...tierForm, monthly_price: e.target.value })}
                            placeholder="2500"
                            required
                          />
                        </div>
                        <div>
                          <Label>Annual Discount (%)</Label>
                          <Input
                            type="number"
                            value={tierForm.annual_discount_percentage}
                            onChange={(e) => setTierForm({ ...tierForm, annual_discount_percentage: e.target.value })}
                            placeholder="10"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Input
                          value={tierForm.description}
                          onChange={(e) => setTierForm({ ...tierForm, description: e.target.value })}
                          placeholder="R5m - R20m projects"
                        />
                      </div>
                      <Button type="submit" className="w-full">
                        {editingTier ? 'Update Tier' : 'Create Tier'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {tiers.map((tier) => (
                  <div key={tier.id} className="border rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <h4 className="font-semibold">{tier.name}</h4>
                        {!tier.is_active && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Inactive</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">
                        {formatCurrency(tier.min_value)} - {tier.max_value ? formatCurrency(tier.max_value) : 'Unlimited'}
                      </p>
                      {tier.description && <p className="text-xs text-slate-400 mt-1">{tier.description}</p>}
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-lg">{formatCurrency(tier.monthly_price)}/mo</p>
                        {tier.annual_price && (
                          <p className="text-sm text-slate-500">{formatCurrency(tier.annual_price)}/yr</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditTier(tier)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteTier(tier)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {tiers.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-slate-500">No billing tiers configured</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Settings Dialog */}
        <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Billing Settings</DialogTitle>
            </DialogHeader>
            {settings && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>VAT Rate (%)</Label>
                    <Input
                      type="number"
                      value={settings.vat_rate}
                      onChange={(e) => setSettings({ ...settings, vat_rate: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Payment Terms (days)</Label>
                    <Input
                      type="number"
                      value={settings.default_payment_terms}
                      onChange={(e) => setSettings({ ...settings, default_payment_terms: parseInt(e.target.value) })}
                    />
                  </div>
                </div>
                <div>
                  <Label>VAT Number</Label>
                  <Input
                    value={settings.vat_number || ''}
                    onChange={(e) => setSettings({ ...settings, vat_number: e.target.value })}
                    placeholder="Your VAT registration number"
                  />
                </div>
                <div>
                  <Label>Company Name (for invoices)</Label>
                  <Input
                    value={settings.company_name || ''}
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Company Email</Label>
                  <Input
                    value={settings.company_email || ''}
                    onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Bank Details</Label>
                  <Textarea
                    value={settings.bank_details || ''}
                    onChange={(e) => setSettings({ ...settings, bank_details: e.target.value })}
                    placeholder="Bank name, Account number, Branch code"
                    rows={3}
                  />
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Suspension Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Warning after (days)</Label>
                      <Input
                        type="number"
                        value={settings.warning_days}
                        onChange={(e) => setSettings({ ...settings, warning_days: parseInt(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Suspend after (days)</Label>
                      <Input
                        type="number"
                        value={settings.suspension_days}
                        onChange={(e) => setSettings({ ...settings, suspension_days: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>
                </div>
                <Button 
                  className="w-full"
                  onClick={async () => {
                    try {
                      await billingApi.updateSettings(settings);
                      toast.success('Settings saved');
                      setSettingsDialogOpen(false);
                      loadData();
                    } catch (error) {
                      toast.error('Failed to save settings');
                    }
                  }}
                >
                  Save Settings
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Invoice Creation Dialog */}
        {/* Use new Invoice Create Dialog component */}
        <InvoiceCreateDialog
          open={invoiceDialogOpen}
          onOpenChange={setInvoiceDialogOpen}
          clients={clients}
          tiers={tiers}
          settings={settings}
          onSubmit={handleCreateInvoice}
        />

        {/* Invoice View Dialog */}
        <InvoiceViewDialog
          open={viewInvoiceOpen}
          onOpenChange={(open) => { setViewInvoiceOpen(open); if (!open) setViewInvoice(null); }}
          invoice={viewInvoice}
          settings={settings}
          onSend={handleSendInvoice}
          onResend={handleResendInvoice}
          onEdit={openEditInvoice}
          onRecordPayment={openPaymentDialog}
          onVoid={handleVoidInvoice}
        />

        {/* Record Payment Dialog */}
        <RecordPaymentDialog
          open={paymentDialogOpen}
          onOpenChange={(open) => { setPaymentDialogOpen(open); if (!open) setPaymentInvoice(null); }}
          invoice={paymentInvoice}
          onSubmit={handleRecordPayment}
        />

        {/* Edit Invoice Dialog (reuse Create dialog with pre-filled data) */}
        {editInvoice && (
          <Dialog open={editInvoiceOpen} onOpenChange={(open) => { setEditInvoiceOpen(open); if (!open) setEditInvoice(null); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-invoice-dialog">
              <DialogHeader>
                <DialogTitle className="text-sm font-bold uppercase tracking-wider">Edit Invoice — {editInvoice.invoice_number}</DialogTitle>
              </DialogHeader>
              <EditInvoiceForm
                invoice={editInvoice}
                settings={settings}
                onSave={(data) => handleUpdateInvoice(editInvoice.id, data)}
                onCancel={() => setEditInvoiceOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </DashboardLayout>
  );
}

function InvoicesTableInline({ invoices, onViewInvoice, onSendInvoice, onResendInvoice, onEditInvoice, onRecordPayment, onVoidInvoice }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-emerald-100 text-emerald-700';
      case 'sent': return 'bg-blue-100 text-blue-700';
      case 'draft': return 'bg-slate-100 text-slate-600';
      case 'overdue': return 'bg-red-100 text-red-700';
      case 'partially_paid': return 'bg-amber-100 text-amber-700';
      case 'void': return 'bg-slate-100 text-slate-400';
      default: return 'bg-slate-100 text-slate-600';
    }
  };
  return (
    <div className="overflow-x-auto" data-testid="invoices-table">
      <table className="w-full">
        <thead>
          <tr className="border-b bg-slate-50">
            <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Invoice</th>
            <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Client</th>
            <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Date</th>
            <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Due Date</th>
            <th className="text-right py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Amount</th>
            <th className="text-right py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Balance</th>
            <th className="text-center py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">VAT</th>
            <th className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
            <th className="text-right py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr key={invoice.id} className="border-b last:border-0 hover:bg-slate-50/50 transition-colors">
              <td className="py-3 px-3 font-mono text-sm font-medium">{invoice.invoice_number}</td>
              <td className="py-3 px-3 text-sm">{invoice.client_name}</td>
              <td className="py-3 px-3 text-sm text-slate-500">{invoice.invoice_date}</td>
              <td className="py-3 px-3 text-sm text-slate-500">{invoice.due_date}</td>
              <td className="py-3 px-3 font-semibold text-right text-sm">R{(invoice.total || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</td>
              <td className="py-3 px-3 font-semibold text-right text-sm">
                {invoice.balance_due > 0 ? <span className="text-red-600">R{(invoice.balance_due).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span> : <span className="text-emerald-600">R0.00</span>}
              </td>
              <td className="py-3 px-3 text-center">
                <span className={`text-xs font-medium ${invoice.include_vat === false ? 'text-slate-400' : 'text-slate-600'}`}>
                  {invoice.include_vat === false ? 'No' : `${invoice.tax_rate || 15}%`}
                </span>
              </td>
              <td className="py-3 px-3">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${getStatusColor(invoice.status)}`}>
                  {invoice.status?.replace('_', ' ')}
                </span>
              </td>
              <td className="py-3 px-3 text-right">
                <div className="flex justify-end gap-0.5">
                  <button onClick={() => onViewInvoice?.(invoice)} className="p-1.5 rounded hover:bg-slate-100" title="View" data-testid={`view-inv-${invoice.invoice_number}`}>
                    <Eye className="h-3.5 w-3.5 text-slate-500" />
                  </button>
                  {invoice.status === 'draft' && (
                    <>
                      <button onClick={() => onEditInvoice?.(invoice)} className="p-1.5 rounded hover:bg-slate-100" title="Edit" data-testid={`edit-inv-${invoice.invoice_number}`}>
                        <Edit2 className="h-3.5 w-3.5 text-slate-500" />
                      </button>
                      <button onClick={() => onSendInvoice?.(invoice)} className="p-1.5 rounded hover:bg-blue-50" title="Send" data-testid={`send-inv-${invoice.invoice_number}`}>
                        <Send className="h-3.5 w-3.5 text-blue-600" />
                      </button>
                    </>
                  )}
                  {(invoice.status === 'sent' || invoice.status === 'overdue') && (
                    <>
                      <button onClick={() => onResendInvoice?.(invoice)} className="p-1.5 rounded hover:bg-slate-100" title="Resend">
                        <RefreshCw className="h-3.5 w-3.5 text-slate-500" />
                      </button>
                      <button onClick={() => onRecordPayment?.(invoice)} className="p-1.5 rounded hover:bg-emerald-50" title="Record Payment">
                        <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                      </button>
                    </>
                  )}
                  {invoice.status === 'partially_paid' && (
                    <button onClick={() => onRecordPayment?.(invoice)} className="p-1.5 rounded hover:bg-emerald-50" title="Record Payment">
                      <DollarSign className="h-3.5 w-3.5 text-emerald-600" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EditInvoiceForm({ invoice, settings, onSave, onCancel }) {
  const [form, setForm] = useState({
    invoice_date: invoice.invoice_date || '',
    due_date: invoice.due_date || '',
    include_vat: invoice.include_vat !== false,
    tax_rate: invoice.tax_rate || 15,
    discount_amount: invoice.discount_amount || 0,
    billing_period_start: invoice.billing_period_start || '',
    billing_period_end: invoice.billing_period_end || '',
    notes: invoice.notes || '',
    line_items: (invoice.line_items || []).map(item => ({
      description: item.description || '',
      quantity: item.quantity || 1,
      unit_price: item.unit_price || 0,
    })),
  });
  const [saving, setSaving] = useState(false);

  const updateLineItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, line_items: items };
    });
  };

  const subtotal = form.line_items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0), 0);
  const taxAmount = form.include_vat ? (subtotal - (parseFloat(form.discount_amount) || 0)) * ((parseFloat(form.tax_rate) || 0) / 100) : 0;
  const total = subtotal - (parseFloat(form.discount_amount) || 0) + taxAmount;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        invoice_date: form.invoice_date || undefined,
        due_date: form.due_date || undefined,
        include_vat: form.include_vat,
        tax_rate: parseFloat(form.tax_rate) || 0,
        discount_amount: parseFloat(form.discount_amount) || 0,
        billing_period_start: form.billing_period_start || undefined,
        billing_period_end: form.billing_period_end || undefined,
        notes: form.notes || undefined,
        line_items: form.line_items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          amount: (parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0),
          tax_rate: form.include_vat ? parseFloat(form.tax_rate) || 0 : 0,
          tax_amount: form.include_vat ? ((parseFloat(item.quantity) || 1) * (parseFloat(item.unit_price) || 0)) * ((parseFloat(form.tax_rate) || 0) / 100) : 0,
        })),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="edit-invoice-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Invoice Date</Label>
          <Input type="date" value={form.invoice_date} onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))} />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Due Date</Label>
          <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Service Period Start</Label>
          <Input type="date" value={form.billing_period_start} onChange={e => setForm(f => ({ ...f, billing_period_start: e.target.value }))} />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Service Period End</Label>
          <Input type="date" value={form.billing_period_end} onChange={e => setForm(f => ({ ...f, billing_period_end: e.target.value }))} />
        </div>
      </div>
      <div className="flex items-center justify-between p-3 bg-slate-50 border rounded-lg">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Include VAT</Label>
          <p className="text-xs text-slate-500 mt-0.5">{form.include_vat ? `VAT at ${form.tax_rate}%` : 'No VAT'}</p>
        </div>
        <Switch checked={form.include_vat} onCheckedChange={v => setForm(f => ({ ...f, include_vat: v }))} />
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider mb-2 block">Line Items</Label>
        {form.line_items.map((item, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-end mb-2">
            <div className="col-span-6">
              <Input value={item.description} onChange={e => updateLineItem(idx, 'description', e.target.value)} placeholder="Description" />
            </div>
            <div className="col-span-2">
              <Input type="number" value={item.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} />
            </div>
            <div className="col-span-3">
              <Input type="number" step="0.01" value={item.unit_price} onChange={e => updateLineItem(idx, 'unit_price', e.target.value)} />
            </div>
            <div className="col-span-1">
              {form.line_items.length > 1 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }))} className="text-red-500 p-1">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
        <Button type="button" variant="ghost" size="sm" onClick={() => setForm(f => ({ ...f, line_items: [...f.line_items, { description: '', quantity: 1, unit_price: 0 }] }))}>
          <Plus className="h-3 w-3 mr-1" />Add Item
        </Button>
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider">Notes</Label>
        <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
      </div>
      <div className="bg-slate-900 text-white rounded-lg p-4 space-y-1">
        <div className="flex justify-between text-sm"><span className="text-slate-400">Subtotal</span><span>R{subtotal.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span></div>
        {form.include_vat && <div className="flex justify-between text-sm"><span className="text-slate-400">VAT ({form.tax_rate}%)</span><span>R{taxAmount.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span></div>}
        <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-700"><span>Total</span><span>R{total.toLocaleString('en-ZA', { minimumFractionDigits: 2 })}</span></div>
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} data-testid="save-edit-invoice-btn">
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
}
